import { UserResponseDtoRole } from "@/types/api";
import type { User } from "@/types/user";
import { isSubAdmin, isSuperAdmin } from "@/lib/auth-utils";

/** Platform owner role (API: SUPER_ADMIN). */
export const PLATFORM_OWNER_ROLE = UserResponseDtoRole.SUPER_ADMIN;

// Specs §4.2: a Sub-Admin assigns contributors — i.e. adds and manages Editors only.
const SUB_ADMIN_ASSIGNABLE = new Set<string>([UserResponseDtoRole.EDITOR]);

/** Roles visible in the users list and role filter for a Sub-Admin. */
const SUB_ADMIN_LISTABLE = new Set<string>([
  UserResponseDtoRole.SUB_ADMIN,
  UserResponseDtoRole.EDITOR,
]);

export function isPlatformOwnerRole(role: string | undefined | null): boolean {
  return role === PLATFORM_OWNER_ROLE;
}

/** Whether the signed-in actor may edit the target user in the UI. */
export function actorMayManageUser(
  actor: User | null | undefined,
  target: Pick<User, "role">,
): boolean {
  if (!actor) return false;
  if (isSuperAdmin(actor)) return true;
  if (isSubAdmin(actor)) {
    return SUB_ADMIN_ASSIGNABLE.has(target.role);
  }
  return false;
}

/** Role ids a SUB_ADMIN may assign; SUPER_ADMIN sees all roles. */
export function filterRolesAssignableByActor<T extends { role: string }>(
  actor: User | null | undefined,
  roles: T[],
): T[] {
  if (!actor || isSuperAdmin(actor)) return roles;
  if (isSubAdmin(actor)) {
    return roles.filter((r) => SUB_ADMIN_ASSIGNABLE.has(r.role));
  }
  return roles.filter((r) => r.role === UserResponseDtoRole.EDITOR);
}

/** Role options in the users list filter; SUPER_ADMIN sees all roles. */
export function filterRolesVisibleInUserList<T extends { role: string }>(
  actor: User | null | undefined,
  roles: T[],
): T[] {
  if (!actor || isSuperAdmin(actor)) return roles;
  if (isSubAdmin(actor)) {
    return roles.filter((r) => SUB_ADMIN_LISTABLE.has(r.role));
  }
  return roles;
}
